import type { ActionFunctionArgs } from "react-router-dom";
import { membershipAPI, APIError } from "../../lib/api";
import { invitePodcastSchema, updateMembershipSchema } from "../../schemas";
import { extractFieldErrors, parseFormData } from "../../utils";
import type { ActionResult } from "./auth.actions";

export async function invitePodcastAction({
  request,
  params,
}: ActionFunctionArgs): Promise<ActionResult> {
  const { id: orgId, networkId } = params;

  if (!orgId || !networkId) {
    return { success: false, error: "Organization ID and Network ID are required" };
  }

  const formData = await parseFormData(request);
  const result = invitePodcastSchema.safeParse(formData);

  if (!result.success) {
    return { success: false, fieldErrors: extractFieldErrors(result.error) };
  }

  try {
    const membership = await membershipAPI.invite(orgId, networkId, {
      podcastId: result.data.podcastId,
      tierId: result.data.tierId || undefined,
      revenueSplitPercent: result.data.revenueSplitPercent,
    });
    return { success: true, data: membership };
  } catch (error) {
    if (error instanceof APIError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: "An unexpected error occurred" };
  }
}

export async function acceptMembershipAction({
  params,
}: ActionFunctionArgs): Promise<ActionResult> {
  const { id: orgId, networkId, membershipId } = params;

  if (!orgId || !networkId || !membershipId) {
    return {
      success: false,
      error: "Organization ID, Network ID, and Membership ID are required",
    };
  }

  try {
    const membership = await membershipAPI.accept(orgId, networkId, membershipId);
    return { success: true, data: membership };
  } catch (error) {
    if (error instanceof APIError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: "An unexpected error occurred" };
  }
}

export async function rejectMembershipAction({
  params,
}: ActionFunctionArgs): Promise<ActionResult> {
  const { id: orgId, networkId, membershipId } = params;

  if (!orgId || !networkId || !membershipId) {
    return {
      success: false,
      error: "Organization ID, Network ID, and Membership ID are required",
    };
  }

  try {
    const membership = await membershipAPI.reject(orgId, networkId, membershipId);
    return { success: true, data: membership };
  } catch (error) {
    if (error instanceof APIError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: "An unexpected error occurred" };
  }
}

export async function leaveMembershipAction({
  request,
  params,
}: ActionFunctionArgs): Promise<ActionResult> {
  const { id: orgId, networkId, membershipId } = params;

  if (!orgId || !networkId || !membershipId) {
    return {
      success: false,
      error: "Organization ID, Network ID, and Membership ID are required",
    };
  }

  try {
    const formData = await request.formData();
    const podcastId = formData.get("podcastId") as string;
    if (!podcastId) {
      return { success: false, error: "Podcast ID is required" };
    }
    const membership = await membershipAPI.leave(orgId, networkId, membershipId, podcastId);
    return { success: true, data: membership };
  } catch (error) {
    if (error instanceof APIError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: "An unexpected error occurred" };
  }
}

export async function updateMembershipSplitAction({
  request,
  params,
}: ActionFunctionArgs): Promise<ActionResult> {
  const { id: orgId, networkId, membershipId } = params;

  if (!orgId || !networkId || !membershipId) {
    return {
      success: false,
      error: "Organization ID, Network ID, and Membership ID are required",
    };
  }

  const formData = await parseFormData(request);
  const result = updateMembershipSchema.safeParse(formData);

  if (!result.success) {
    return { success: false, fieldErrors: extractFieldErrors(result.error) };
  }

  try {
    const membership = await membershipAPI.updateSplit(orgId, networkId, membershipId, {
      tierId: result.data.tierId || undefined,
      revenueSplitPercent: result.data.revenueSplitPercent,
    });
    return { success: true, data: membership };
  } catch (error) {
    if (error instanceof APIError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: "An unexpected error occurred" };
  }
}
